import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowRight, FileText, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../context/LanguageContext";

export default function Login() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('common.error'));
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('auth.loginSuccess'));
      
      // Redirect based on onboarding status
      if (!user.onboarding_completed) {
        navigate("/onboarding");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F7FAF9]">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-10">
            <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-14 w-auto" />
          </Link>

          <h1 className="text-2xl font-heading font-bold text-slate-800 mb-2">Sign in</h1>
          <p className="text-slate-500 mb-8">
            Enter your email to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="pl-10 h-11 bg-white border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary font-medium hover:text-primary-700">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 pr-10 h-11 bg-white border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full h-11 rounded-full bg-primary hover:bg-primary-700 shadow-primary font-medium" disabled={loading} data-testid="signin-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Sign in
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-8">
            Don't have an account?{" "}
            <Link to="/pricing" className="text-primary font-semibold hover:text-primary-700">
              Subscribe now
            </Link>
          </p>
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary to-primary-800 items-center justify-center p-12 relative overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>
        
        <div className="max-w-md text-white relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/15 rounded-full backdrop-blur-sm">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs font-medium">AI-Powered Documents</span>
            </div>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4 leading-tight">
            Create professional invoices, receipts & more
          </h2>
          <p className="text-white/70 mb-10 text-base">
            Generate beautiful business documents in minutes. Track payments, manage clients, and stay on top of your cash flow.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-4 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <FileText className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium">3 document types</span>
            </div>
            <div className="flex items-center gap-4 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium">AI auto-fill & error detection</span>
            </div>
            <div className="flex items-center gap-4 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium">Track pending & paid invoices</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
